import {
  ApiResultCamelCase,
  ApiResultTransform,
  UpsApiHttpErrorResponse,
  UpsApiHttpErrorResponseCamelCase,
} from '@ups/common';
import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { LogService } from '@ups/xplat/core';
import { ToastrService } from 'ngx-toastr';
import {
  decideAuth401Reload,
  readLastAuthReloadAt,
  writeLastAuthReloadAt,
} from './web-error-handler-401-reload';

/** One structured console.error payload per handled failure (#31316 replay noise). */
interface IGlobalErrorHandlerDiagnostic {
  counter: number;
  originalResponse: unknown;
  unwrappedResponse: unknown;
  transformedError: ApiResultCamelCase;
  http: {
    status?: number;
    statusText?: string;
    url?: string;
    body?: unknown;
  };
  toast: {
    shown: boolean;
    message?: string;
    skippedReason?: string;
    toastError?: unknown;
  };
  auth401?: {
    reload: boolean;
    reason?: string;
  };
}

@Injectable()
export class GlobalErrorHandler extends ErrorHandler {
  injector = inject(Injector);
  log = inject(LogService);
  // Note: cannot inject toastr in error handler directly
  // https://github.com/scttcper/ngx-toastr/issues/179#issuecomment-325724269
  private counter = 0;

  private get toastr(): ToastrService {
    return this.injector.get(ToastrService);
  }

  /**
   * To avoid error handling at every http call to show message to the user - don't catch it in the component and it will be caught here.
   * If you need to catch it to update component state then rethrow the error so it will be caught here nad will show toast popup message with the error.
   */
  handleError(
    /* eslint-disable @typescript-eslint/naming-convention */
    response: (UpsApiHttpErrorResponse | UpsApiHttpErrorResponseCamelCase) & {
      rejection: (
        | UpsApiHttpErrorResponse
        | UpsApiHttpErrorResponseCamelCase
      ) & { StatusCode?: number | string; statusCode?: number | string };
    }
    /* eslint-enable @typescript-eslint/naming-convention */
  ) {
    // FIRST: Check if this is a "login_required" error from Auth0 on a public route
    // These errors are expected and should be silently ignored (no logging, no toast)
    const unwrappedResponseForCheck =
      response.rejection && response.rejection.error
        ? response.rejection
        : response;

    const errorString = String(unwrappedResponseForCheck?.error || '');
    const messageString = String(unwrappedResponseForCheck?.message || '');
    const isAuth0LoginRequired =
      errorString === 'login_required' ||
      errorString.includes('login_required') ||
      messageString.includes('Login required') ||
      (unwrappedResponseForCheck &&
        unwrappedResponseForCheck.constructor?.name === 'GenericError');

    // /login is in this list because the interactive-login redirect below
    // must never fire while the user is ON a login surface: the dual-issuer
    // login screen triggers background login_required errors by existing
    // (any silent token check on it fails until the user signs in), and
    // answering those by redirecting to Auth0 interactive login yanked
    // users off the screen mid-typing and made the Entra path unreachable.
    const isPublicRoute =
      typeof window !== 'undefined' &&
      (window.location.pathname.startsWith('/public/') ||
        window.location.pathname.startsWith('/login'));

    if (isAuth0LoginRequired && isPublicRoute) {
      // Silently ignore - no logging, no toast, just return
      return;
    }

    // SECOND: Auth0 "interaction required" errors thrown by silent token
    // renewal (getTokenSilently). These are unrecoverable without user
    // interaction - notably Auth0 never skips the consent check for a
    // localhost origin, so local serves hit `consent_required` on every
    // silent renewal once the in-memory token expires. Without this branch
    // the app strands with every API call failing until a manual reload +
    // login. Re-run the interactive flow and return to the current page.
    const interactionRequiredCodes = [
      'consent_required',
      'login_required',
      'interaction_required',
    ];
    const isInteractionRequired =
      interactionRequiredCodes.some((code) => errorString.includes(code)) ||
      messageString.includes('Consent required');

    // Issuer guard: when the session's last-used issuer is Entra, an Auth0
    // interaction-required error is EXPECTED NOISE (any Auth0 silent check
    // fails for an MSAL session), and answering it with the Auth0
    // interactive flow silently converts the user's Entra session into an
    // Auth0 one mid-use. Key and values are the login-issuer store's
    // ('ups.login.issuer', libs/xplat/web/features auth/login-issuer.store.ts);
    // read directly because web/core cannot depend on web/features.
    const isEntraSession =
      typeof window !== 'undefined' &&
      window.localStorage.getItem('ups.login.issuer') === 'entra';

    if (isInteractionRequired && isEntraSession) {
      console.warn(
        '[GLOBAL ERROR HANDLER] Auth0 interaction-required error ignored: active session issuer is Entra'
      );
      return;
    }

    if (isInteractionRequired && !isPublicRoute) {
      // Loop guard: if a redirect was already attempted moments ago, fall
      // through to normal logging instead of bouncing forever.
      const redirectStampKey = 'auth0_interactive_redirect_at';
      const lastRedirect = Number(
        sessionStorage.getItem(redirectStampKey) || 0
      );
      if (Date.now() - lastRedirect > 30000) {
        const auth = this.injector.get(AuthService, null);
        if (auth) {
          sessionStorage.setItem(redirectStampKey, String(Date.now()));
          console.warn(
            '[GLOBAL ERROR HANDLER] Auth0 interaction required (' +
              errorString +
              ') - redirecting to interactive login'
          );
          auth.loginWithRedirect({
            appState: {
              target: window.location.pathname + window.location.search,
            },
          });
          return;
        }
      }
    }

    // If we got here, it's a real error that needs to be logged and handled
    const unwrappedResponse =
      response.rejection && response.rejection.error
        ? response.rejection
        : response; // Workaround to ZoneJs wrapping error to rejection property when throw from promise. Bug: https://github.com/angular/angular/issues/27840
    const counter = ++this.counter;

    // TODO: cretae a special sink for error that would log to the helpdesk...
    // const helpDesk: HelpDeskDTO = {
    //     FullName: null,
    //     Email: null,
    //     Phone: null,
    //     SmartlookURL: null,
    //     SupportInfo: error.stack,
    //     Body: error.message,
    //     RelativeURL: window.location.href,
    //     // ErrorHistory: this.errorHistory,
    //     ShortDescription: error.message
    // };

    // Error obj.:
    // - error.message
    // - error.stack()
    // We show only the toast popup message by default only if it is a and error from http call - from our API that returns extended http error - the UpsApiHttpErrorResponse
    // We don't show toast popup message for bugs or console errors - which end up here as well.
    // We show to user only first message from error.error.Messages as usually it contains most meaning full error message from the most inner exception sent by Api.
    const error: ApiResultCamelCase = {
      ...ApiResultTransform.toCamelCase(
        (unwrappedResponse as UpsApiHttpErrorResponse).error
      ),
      ...ApiResultTransform.toCamelCase(
        (unwrappedResponse as UpsApiHttpErrorResponseCamelCase).error
      ),
    };

    const diagnostic: IGlobalErrorHandlerDiagnostic = {
      counter,
      originalResponse: response,
      unwrappedResponse,
      transformedError: error,
      http: {
        status: unwrappedResponse?.status,
        statusText: unwrappedResponse?.statusText,
        url: unwrappedResponse?.url,
        body: unwrappedResponse?.error,
      },
      toast: { shown: false },
    };

    const errorMessage = `The error above was Caught in Global Error Handler`;

    if (error.message || error.messages?.length || unwrappedResponse?.error) {
      // Please see information on detail/Detail problem on :
      // https://docs.microsoft.com/en-us/dotnet/api/microsoft.aspnetcore.mvc.problemdetails?view=aspnetcore-6.0
      let messageForUser =
        unwrappedResponse?.error['detail'] ??
        unwrappedResponse?.error['Detail'] ??
        error.message ??
        unwrappedResponse?.statusText;

      if (error.messages?.length > 0) {
        messageForUser = error.messages[0];

        if (error.messages.length > 1) {
          const additionalMessages = error.messages
            .slice(1)
            .filter((m) => m?.toLowerCase().indexOf('inner exception') === -1);

          messageForUser =
            messageForUser + '; ' + additionalMessages.join('; ');
        }
      }

      if (this.toastr) {
        try {
          this.toastr.error(messageForUser, 'Error occurred', {
            closeButton: true,
          });

          diagnostic.toast = { shown: true, message: messageForUser };

          this.log.error(
            unwrappedResponse,
            `${errorMessage} and a toast message was shown to user as the error came from our UPS Api. Global Error counter #: ${counter} Message:${messageForUser}`
          );
        } catch (err) {
          diagnostic.toast = {
            shown: false,
            message: messageForUser,
            toastError: err,
          };
          this.log.debug(
            unwrappedResponse,
            `${errorMessage}. Global Error counter #: ${counter}`
          );
          this.log.debug(err);
          this.log.debug(
            `You can likely ignore this error. This just means this application has not added ngx-toastr with the proper CSS styling. If you want toast error messages to appear in the UI, you can add it.`
          );
        }
      } else {
        diagnostic.toast = {
          shown: false,
          message: messageForUser,
          skippedReason: 'no toastr service',
        };
        this.log.debug(
          unwrappedResponse,
          `${errorMessage}. Global Error counter #: ${counter}`
        );
      }
    } else {
      diagnostic.toast = {
        shown: false,
        skippedReason: 'no message/messages on error',
      };
      this.log.debug(
        unwrappedResponse,
        `${errorMessage}. Global Error counter #: ${counter}`
      );
    }

    // if (
    //   response.rejection &&
    //   (response.rejection.StatusCode?.toString() === '401' ||
    //     response.rejection.statusCode?.toString() === '401')
    // ) {
    //   console.log('response.rejection.StatusCode:', response.rejection.StatusCode)
    //   localStorage.removeItem('access_token');
    //   localStorage.removeItem('id_token');
    //   localStorage.removeItem('expires_at');
    //   localStorage.removeItem('profile');

    //   // Go back to the home route
    //   // this.router.navigate(['/']);

    //   window.location.reload();
    // }

    /*
         Rethrowing the error in the GlobalErrorHandler leads to an uncaught exception where the browser stops execution. That's why it works only once.
         The solution is simply to not rethrow in the ErrorHandler.

         More detail: In this case there is no onError-function provided in subscribe(). That's why rxjs/Subscriber.js runs into the catch-part of __tryOrUnsub. It tries to execute the onError-function but fails (because there is none) and therefore unsubscribes from the Observable and throws the error. The GlobalErrorHandler picks it up, does logging and notification and then rethrows it.
         At this point the error becomes an uncaught exception outside of Angular. (the default ErrorHandler does not rethrow an error)
         */

    if (
      (response && response.status?.toString() === '401') ||
      (response.rejection &&
        (response.rejection.StatusCode?.toString() === '401' ||
          response.rejection.statusCode?.toString() === '401' ||
          response.rejection.status?.toString() === '401'))
    ) {
      // #30948: an unconditional reload here turns any RECURRING 401 into an infinite
      // reload loop. Telemetry 401s never reload; everything else reloads at most once
      // per window (sessionStorage carries the guard across the reload it causes).
      const requestUrl =
        (response as { url?: string })?.url ??
        (response.rejection as { url?: string })?.url;
      const storage =
        typeof window !== 'undefined' ? window.sessionStorage : undefined;
      const decision = decideAuth401Reload(
        requestUrl,
        readLastAuthReloadAt(storage),
        Date.now()
      );
      if (decision.reload) {
        diagnostic.auth401 = { reload: true };
        writeLastAuthReloadAt(storage, Date.now());
        localStorage.removeItem('access_token');
        localStorage.removeItem('id_token');
        localStorage.removeItem('expires_at');
        localStorage.removeItem('profile');
        // Go back to the home route
        // this.router.navigate(['/']);
        window.location.reload();
      } else {
        diagnostic.auth401 = { reload: false, reason: decision.reason };
      }
    }

    console.error('[GLOBAL ERROR HANDLER]', diagnostic);
    /*
     Rethrowing the error in the GlobalErrorHandler leads to an uncaught exception where the browser stops execution. That's why it works only once.
     The solution is simply to not rethrow in the ErrorHandler.

     More detail: In this case there is no onError-function provided in subscribe(). That's why rxjs/Subscriber.js runs into the catch-part of __tryOrUnsub. It tries to execute the onError-function but fails (because there is none) and therefore unsubscribes from the Observable and throws the error. The GlobalErrorHandler picks it up, does logging and notification and then rethrows it.
     At this point the error becomes an uncaught exception outside of Angular. (the default ErrorHandler does not rethrow an error)
     */
  }
}
